import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  ExternalLink,
  FileDown,
  Loader2,
  RefreshCw,
  Search,
  Shield,
} from 'lucide-react';

import {
  getCompliancePortfolio,
  openHipaaReport,
  type CompliancePortfolioResponse,
  type CompliancePortfolioRow,
  type ComplianceFramework,
} from '../../services/api';

// ----------------------------------------------------------------------------
// Framework tabs — HIPAA live, others coming soon
// ----------------------------------------------------------------------------

const FRAMEWORKS: { key: ComplianceFramework; label: string; available: boolean }[] = [
  { key: 'hipaa', label: 'HIPAA', available: true },
  { key: 'soc2', label: 'SOC 2', available: false },
  { key: 'pci', label: 'PCI DSS', available: false },
  { key: 'gdpr', label: 'GDPR', available: false },
];

function scoreColor(score: number): string {
  if (score >= 90) return 'text-green-600 dark:text-green-400';
  if (score >= 70) return 'text-yellow-600 dark:text-yellow-400';
  return 'text-red-600 dark:text-red-400';
}

function stalenessBadge(s: CompliancePortfolioRow['staleness']): { label: string; cls: string } {
  switch (s) {
    case 'fresh':        return { label: 'Fresh',        cls: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' };
    case 'stale':        return { label: 'Stale',        cls: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300' };
    case 'very_stale':   return { label: 'Very stale',   cls: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300' };
    case 'never_synced': return { label: 'Never synced', cls: 'bg-muted text-muted-foreground' };
  }
}

function formatWhen(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

// ----------------------------------------------------------------------------
// Stat tile
// ----------------------------------------------------------------------------

function StatTile({ label, value, sub, valueClass }: { label: string; value: string | number; sub?: string; valueClass?: string }) {
  return (
    <div className="border border-border rounded-lg bg-card p-4">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`text-3xl font-bold mt-1 ${valueClass ?? 'text-foreground'}`}>{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </div>
  );
}

// ----------------------------------------------------------------------------
// Page
// ----------------------------------------------------------------------------

export default function CompliancePortfolioPage() {
  const navigate = useNavigate();
  const [framework, setFramework] = useState<ComplianceFramework>('hipaa');
  const [data, setData] = useState<CompliancePortfolioResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [reportBusy, setReportBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getCompliancePortfolio(framework);
      setData(result);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load compliance portfolio');
    } finally {
      setLoading(false);
    }
  }, [framework]);

  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => {
    if (!data) return [];
    const q = query.trim().toLowerCase();
    return q ? data.rows.filter(r => r.clientName.toLowerCase().includes(q)) : data.rows;
  }, [data, query]);

  const generateReport = useCallback(async (clientId: string) => {
    setReportBusy(clientId);
    try {
      await openHipaaReport(clientId);
    } catch {
      // openHipaaReport opens a tab; failures are rare (no snapshot). Silent — the
      // per-client page surfaces the detailed error.
    } finally {
      setReportBusy(null);
    }
  }, []);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <Shield className="h-8 w-8 text-primary-500" />
          <div>
            <h1 className="text-2xl font-semibold text-foreground">Compliance</h1>
            <div className="text-sm text-muted-foreground">Posture across all Microsoft 365 clients</div>
          </div>
        </div>
        <button
          onClick={load}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-border bg-card hover:bg-accent text-sm"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Reload
        </button>
      </div>

      {/* Framework tabs */}
      <div className="flex items-center gap-1 border-b border-border">
        {FRAMEWORKS.map(f => (
          <button
            key={f.key}
            disabled={!f.available}
            onClick={() => f.available && setFramework(f.key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              framework === f.key
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : f.available
                ? 'border-transparent text-muted-foreground hover:text-foreground'
                : 'border-transparent text-muted-foreground/40 cursor-not-allowed'
            }`}
            title={f.available ? undefined : 'Coming soon'}
          >
            {f.label}
            {!f.available && <span className="ml-1.5 text-[10px] uppercase tracking-wide">soon</span>}
          </button>
        ))}
      </div>

      {loading && (
        <div className="flex items-center justify-center py-16 text-muted-foreground">
          <Loader2 className="h-6 w-6 animate-spin mr-2" />
          Loading portfolio…
        </div>
      )}

      {error && !loading && (
        <div className="p-4 rounded-md bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 flex items-center gap-3">
          <AlertCircle className="h-5 w-5" />
          <div>{error}</div>
        </div>
      )}

      {data && !loading && (
        <>
          {/* Stat tiles */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <StatTile label="M365 Clients" value={data.stats.totalClients} sub={`${data.stats.withSnapshot} assessed`} />
            <StatTile
              label="Average Score"
              value={data.stats.averageScore ?? '—'}
              sub="across assessed clients"
              valueClass={data.stats.averageScore != null ? scoreColor(data.stats.averageScore) : undefined}
            />
            <StatTile label="Below 70" value={data.stats.belowThreshold} sub="need attention" valueClass={data.stats.belowThreshold > 0 ? 'text-red-600 dark:text-red-400' : undefined} />
            <StatTile label="Never Assessed" value={data.stats.neverSynced} sub="no snapshot yet" />
            <StatTile label="Data Gaps" value={data.stats.exoStubbed} sub="Exchange data unavailable" valueClass={data.stats.exoStubbed > 0 ? 'text-yellow-600 dark:text-yellow-400' : undefined} />
          </div>

          {/* Search */}
          <div className="relative max-w-sm">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Filter clients…"
              className="w-full pl-9 pr-3 py-2 rounded-md border border-border bg-card text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/40"
            />
          </div>

          {/* Table */}
          <div className="border border-border rounded-lg bg-card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/40">
                    <th className="text-left font-medium px-4 py-2.5">Client</th>
                    <th className="text-center font-medium px-3 py-2.5">Score</th>
                    <th className="text-center font-medium px-3 py-2.5">Must-Fix</th>
                    <th className="text-center font-medium px-3 py-2.5">Should-Fix</th>
                    <th className="text-left font-medium px-3 py-2.5">Snapshot</th>
                    <th className="text-right font-medium px-4 py-2.5">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(r => (
                    <tr
                      key={r.clientId}
                      className="border-b border-border last:border-0 hover:bg-accent/30 cursor-pointer"
                      onClick={() => navigate(`/compliance/${r.clientId}`)}
                    >
                      <td className="px-4 py-2.5">
                        <div className="font-medium text-foreground">{r.clientName}</div>
                        {r.isExoStubbed && (
                          <div className="text-[11px] text-yellow-600 dark:text-yellow-400">Exchange data unavailable</div>
                        )}
                      </td>
                      <td className="text-center px-3 py-2.5">
                        {r.score != null
                          ? <span className={`font-bold ${scoreColor(r.score)}`}>{r.score}</span>
                          : <span className="text-muted-foreground">—</span>}
                      </td>
                      <td className="text-center px-3 py-2.5">
                        {r.hasSnapshot ? <span className={r.mustFix > 0 ? 'text-red-600 dark:text-red-400 font-medium' : 'text-muted-foreground'}>{r.mustFix}</span> : '—'}
                      </td>
                      <td className="text-center px-3 py-2.5">
                        {r.hasSnapshot ? <span className={r.shouldFix > 0 ? 'text-yellow-600 dark:text-yellow-400 font-medium' : 'text-muted-foreground'}>{r.shouldFix}</span> : '—'}
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="flex items-center gap-2">
                          <span className={`inline-block text-[11px] px-2 py-0.5 rounded-full ${stalenessBadge(r.staleness).cls}`}>
                            {stalenessBadge(r.staleness).label}
                          </span>
                          <span className="text-xs text-muted-foreground">{formatWhen(r.lastSynced)}</span>
                        </div>
                      </td>
                      <td className="px-4 py-2.5" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => generateReport(r.clientId)}
                            disabled={!r.hasSnapshot || reportBusy === r.clientId}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-border hover:bg-accent text-xs disabled:opacity-40 disabled:cursor-not-allowed"
                            title={r.hasSnapshot ? 'Generate HIPAA report' : 'No snapshot yet — open the client and Refresh Now first'}
                          >
                            <FileDown className={`h-3.5 w-3.5 ${reportBusy === r.clientId ? 'animate-pulse' : ''}`} />
                            Report
                          </button>
                          <button
                            onClick={() => navigate(`/compliance/${r.clientId}`)}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-border hover:bg-accent text-xs"
                            title="Open compliance detail"
                          >
                            <ExternalLink className="h-3.5 w-3.5" />
                            Open
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {rows.length === 0 && (
                    <tr>
                      <td colSpan={6} className="text-center py-10 text-muted-foreground">
                        {query ? 'No clients match your filter.' : 'No M365 clients found.'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="text-xs text-muted-foreground">
            Generated {new Date(data.generatedAt).toLocaleString()}. Scores reflect the last collected snapshot per client — open a client and use Refresh Now to collect current data.
          </div>
        </>
      )}
    </div>
  );
}
